// Shared QC-failure review chrome — the failure-text block, the rejected
// candidate preview, and the operator vouch (override-qc) control.
//
// Extracted from pages/ProductAds AdDetailModal so every surface that
// shows an ad preview next to QC failure text reuses ONE control.
// POSTs to /api/ads/:id/override-qc. After QC-1 an inspected fail is a
// delivered draft — do NOT gate the vouch on status==='failed'. Paint
// from failure.isQc / shouldPaintQcOutline (never re-derive from
// renderErrorMessage / visionQc client-side). Approve stays ungated on
// the parent; this control is the outline-suppress path, not a reject.
//
// Image URL rule (qcReviewDisplayUrl): video → renderUrl; image →
// photorealUrl || renderUrl; then visionQc.discardedRenderUrl — the last
// vision-QC attempt's own candidate, present only on a genuine failure
// verdict. Both photorealUrl and renderUrl are often null on a QC-failed
// ad, so without that fallback the operator cannot see what they are
// vouching for.

import { useEffect, useState } from 'react';
import {
  Box, Button, HStack, Image, Text, Textarea, useToast
} from '@chakra-ui/react';
import { apiJson } from '../../auth/apiFetch';
import { shouldPaintQcOutline } from '../../lib/qcAdvisoryOutline';

export type QcReviewVisionQc = {
  inspected?: boolean;
  passed?: boolean;
  skipped?: boolean;
  disabled?: boolean;
  reason?: string | null;
  finalAttempt?: number | null;
  regenerated?: boolean;
  summary?: string | null;
  categories?: Record<string, { score: number | null; pass: boolean; findings: string[] }>;
  failureDetail?: string | null;
  // Last vision-QC attempt's own candidate render URL. Present only on a
  // genuine failure verdict (backend: summarizeVisionQc). Null/absent on
  // pass / skip / disabled / pre-gate ads.
  discardedRenderUrl?: string | null;
};

export type QcReviewFailure = {
  label?: string;
  isQc: boolean;
  stage?: string | null;
  keptAsset?: boolean;
  qcCaveat?: string | null;
};

// Minimal ad shape this control needs. Each calling surface maps its own
// local type onto this — no refactor of that surface's state.
export type QcReviewAd = {
  adId: string;
  status: string;
  kind?: string | null;
  photorealUrl?: string | null;
  renderUrl?: string | null;
  posterUrl?: string | null;
  regenerating?: boolean;
  visionQc?: QcReviewVisionQc | null;
  renderErrorMessage?: string | null;
  failure?: QcReviewFailure | null;
  qcOverridden?: boolean;
  qcOverriddenAt?: string | null;
  qcOverriddenBy?: string | null;
  qcOverrideReason?: string | null;
  qcAdvisoryOutline?: boolean;
};

export type QcOverridePatch = {
  status: string;
  qcOverridden: boolean;
  qcOverriddenAt: string | null;
  qcOverriddenBy: string | null;
  qcOverrideReason: string | null;
  // Vouch suppresses the advisory outline locally so the tile does not
  // wait on the next poll. Never fabricate true.
  qcAdvisoryOutline: false;
};

export type QcReviewDisplayAd = {
  kind?: string | null;
  photorealUrl?: string | null;
  renderUrl?: string | null;
  visionQc?: { discardedRenderUrl?: string | null } | null;
};

// Shared URL-priority helper. Used by ProductAds displayUrlFor, the
// legacy Ads displayUrl, AdInspectCard, and the compact preview below.
export function qcReviewDisplayUrl(ad: QcReviewDisplayAd): string | null {
  const primary = ad.kind === 'video'
    ? (ad.renderUrl ?? null)
    : (ad.photorealUrl || ad.renderUrl || null);
  if (primary) return primary;
  return ad.visionQc?.discardedRenderUrl || null;
}

function looksLikeVideoUrl(url: string): boolean {
  return /\/video\/upload\/|\.(mp4|webm|mov|m4v)(\?|$)/i.test(url);
}

export function QcFailureReview({
  ad,
  brandId,
  isRegenerating,
  onMutated,
  showRejectedPreview = true,
  showFailureText = true
}: {
  ad: QcReviewAd;
  brandId: string;
  isRegenerating?: boolean;
  onMutated: (patch: QcOverridePatch) => void;
  // Compact rejected-candidate preview next to the QC text. Default on —
  // that is the "see the image while deciding whether to vouch"
  // requirement. A parent that already renders the same URL in a large
  // chrome frame can pass false to avoid a second copy; Product Ads
  // keeps the default so the reasons stay next to a still-visible frame
  // even after scrolling past the chrome preview.
  showRejectedPreview?: boolean;
  // Product Ads already mounts QcFindingsPanel for advisory drafts.
  // Pass false there so this control does not reprint failureDetail.
  // Surfaces without that panel (AdInspectCard) keep the default.
  showFailureText?: boolean;
}) {
  const toast = useToast();
  const regenerating = isRegenerating ?? !!ad.regenerating;

  const [qcOverrideOpen, setQcOverrideOpen] = useState(false);
  const [qcOverrideReasonText, setQcOverrideReasonText] = useState('');
  const [qcOverrideSubmitting, setQcOverrideSubmitting] = useState(false);

  // A leftover open panel / reason text from ad A must never submit
  // against ad B (this modal instance is reused across ads).
  useEffect(() => {
    setQcOverrideOpen(false);
    setQcOverrideReasonText('');
    setQcOverrideSubmitting(false);
  }, [ad.adId]);

  const failureDetail = ad.visionQc?.failureDetail || null;
  const renderError = ad.renderErrorMessage || null;
  const previewUrl = qcReviewDisplayUrl(ad);
  const qcOutline = shouldPaintQcOutline(ad);
  const isQcFail = !!ad.failure?.isQc || qcOutline;
  // After QC-1, inspected fails are drafts. Historical qc-failed-kept
  // still has status:'failed'. The orange box is the non-findings
  // crash path plus any parent that did not already paint findings.
  const showFailureBox = showFailureText && !regenerating
    && ad.status === 'failed'
    && !!(failureDetail || renderError);
  const showPreview = showRejectedPreview && !!previewUrl && !regenerating
    && (ad.status === 'failed' || isQcFail);
  // Vouch is NOT gated on status==='failed'. Hard render failures have
  // no override path. Approve stays ungated on the parent.
  const showOverride = !regenerating && !ad.qcOverridden && isQcFail;

  const submitOverrideQc = async () => {
    const reason = qcOverrideReasonText.trim();
    if (reason.length < 3) {
      toast({
        title:       'Reason required',
        description: 'Enter at least 3 characters describing why you are vouching for this ad.',
        status:      'warning',
        duration:    4000
      });
      return;
    }
    setQcOverrideSubmitting(true);
    try {
      const res = await apiJson<{
        ad: {
          status:           string;
          qcOverridden:     boolean;
          qcOverriddenAt:   string | null;
          qcOverriddenBy:   string | null;
          qcOverrideReason: string | null;
        }
      }>(
        `/api/ads/${encodeURIComponent(ad.adId)}/override-qc?brandId=${encodeURIComponent(brandId)}`,
        {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ reason })
        }
      );
      onMutated({
        status:             res.ad.status,
        qcOverridden:       res.ad.qcOverridden,
        qcOverriddenAt:     res.ad.qcOverriddenAt,
        qcOverriddenBy:     res.ad.qcOverriddenBy,
        qcOverrideReason:   res.ad.qcOverrideReason,
        qcAdvisoryOutline:  false
      });
      setQcOverrideOpen(false);
      setQcOverrideReasonText('');
      toast({
        title:       'QC outline suppressed',
        description: 'Nothing was re-rendered. This ad stays in Draft.',
        status:      'success',
        duration:    3500
      });
    } catch (e) {
      toast({
        title:       'Override failed',
        description: e instanceof Error ? e.message : String(e),
        status:      'error',
        duration:    6000
      });
    } finally {
      setQcOverrideSubmitting(false);
    }
  };

  if (!showFailureBox && !showPreview && !showOverride && !ad.qcOverridden) return null;

  const asVideo = !!previewUrl && ad.kind === 'video' && looksLikeVideoUrl(previewUrl);
  const previewEl = showPreview && previewUrl ? (
    <Box bg="gray.900" borderRadius="md" overflow="hidden" mt={showFailureBox ? 2 : 0} mb={showFailureBox && (failureDetail || renderError) ? 2 : 0}>
      {asVideo ? (
        <video
          src={previewUrl}
          poster={ad.posterUrl || undefined}
          controls
          muted
          playsInline
          preload="metadata"
          style={{ width: '100%', maxHeight: 220, objectFit: 'contain', display: 'block', background: '#000' }}
        />
      ) : (
        <Image
          src={previewUrl}
          alt="Rejected render"
          w="100%"
          maxH="220px"
          objectFit="contain"
        />
      )}
    </Box>
  ) : null;

  return (
    <>
      {/* Why this ad failed. Owner requirement 2026-08-20: a QC-failed ad
          must show what was wrong with it — using the EXACT text Slack's
          vision-QC alert already carries (visionQc.failureDetail), never a
          second independently-worded description. Falls back to the
          generic renderErrorMessage for a failed ad that never reached
          vision QC. Monospace + pre-wrap for the QC block only:
          buildQcSlackDetail pads/indents its category lines. */}
      {showFailureBox && (
        <Box mt={2} mb={3} p={3} borderWidth="1px" borderColor="orange.300" bg="orange.50" borderRadius="md">
          <Text fontSize="sm" fontWeight="700" color="orange.700">
            {failureDetail ? 'Failed vision QC' : 'Render failed'}
          </Text>
          {previewEl}
          {(failureDetail || renderError) && (
            <Text
              fontSize="11px"
              color="brand.muted"
              mt={1}
              whiteSpace="pre-wrap"
              fontFamily={failureDetail ? 'mono' : undefined}
            >
              {failureDetail || renderError}
            </Text>
          )}
        </Box>
      )}
      {!showFailureBox && previewEl && (
        <Box mt={2} mb={3}>{previewEl}</Box>
      )}

      {/* Operator vouch for a vision-QC fail (POST /:id/override-qc) —
          suppresses the outline; does not re-render. Not gated on
          status==='failed' (new fails are drafts). Hard render
          failures have no override path here. Approve stays ungated:
          publishing a red-outlined draft is how a human disagrees. */}
      {showOverride && (
        <Box mt={2} mb={3} p={3} borderWidth="1px" borderColor="brand.border" borderRadius="md" bg="brand.surface">
          {!qcOverrideOpen ? (
            <HStack justify="space-between">
              <Text fontSize="11px" color="brand.muted">
                Vouch for this ad — suppress the QC outline. Nothing is re-rendered.
              </Text>
              <Button size="sm" variant="outline" onClick={() => setQcOverrideOpen(true)}>
                Vouch for this ad
              </Button>
            </HStack>
          ) : (
            <>
              <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" mb={2}>
                Vouch for this ad
              </Text>
              <Text fontSize="xs" color="brand.muted" mb={2}>
                Required — record why you are suppressing the QC outline. Nothing is
                re-rendered and no generation data changes.
              </Text>
              <Textarea
                value={qcOverrideReasonText}
                onChange={(e) => setQcOverrideReasonText(e.target.value)}
                placeholder="e.g. Reviewed manually — the flagged logo is the product's own branding, not a competitor mark."
                maxLength={1000}
                rows={3}
                size="sm"
                bg="white"
              />
              <HStack justify="flex-end" mt={2} spacing={2}>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setQcOverrideOpen(false); setQcOverrideReasonText(''); }}
                  isDisabled={qcOverrideSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="brand"
                  onClick={() => void submitOverrideQc()}
                  isLoading={qcOverrideSubmitting}
                  isDisabled={qcOverrideReasonText.trim().length < 3}
                >
                  Confirm vouch
                </Button>
              </HStack>
            </>
          )}
        </Box>
      )}

      {/* Persisted QC-override audit note — independent of ad.status,
          so it stays visible even after the ad moves on. Never
          conflated with the Approved badge: an ad can be
          QC-overridden without being approved, or approved without
          ever having been QC-overridden. */}
      {!!ad.qcOverridden && (
        <Box mt={2} mb={3} p={3} borderWidth="1px" borderColor="rsViolet.200" bg="rsViolet.50" borderRadius="md">
          <Text fontSize="sm" fontWeight="700" color="rsViolet.700">
            QC rejection overridden
          </Text>
          <Text fontSize="11px" color="brand.muted" mt={1}>
            {ad.qcOverriddenBy ? `By ${ad.qcOverriddenBy}` : 'By an operator'}
            {ad.qcOverriddenAt ? ` on ${new Date(ad.qcOverriddenAt).toLocaleString()}` : ''}
            {ad.qcOverrideReason ? ` — ${ad.qcOverrideReason}` : ''}
          </Text>
        </Box>
      )}
    </>
  );
}
